import { lazy } from "react";

// Chunk filenames are content-hashed (config-overrides.js) and each deploy
// replaces the whole container, so an open tab's in-memory chunk manifest
// points at files that no longer exist on the server. The next lazy import
// then 404s and webpack throws ChunkLoadError. A fresh index.html only arrives
// via a full page load, so retry a genuine chunk failure once, then do one
// guarded reload. Ordinary module/evaluation errors must reach ErrorBoundry
// immediately; retrying those can repeat side effects and hide code defects.
const RELOAD_GUARD_KEY = "chunk_retry_reloaded_at";
const RELOAD_GUARD_MS = 10 * 60 * 1000;

const CHUNK_LOAD_MESSAGE =
  /(?:Loading (?:CSS )?chunk \S+ failed|Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS for)/i;

export const isChunkLoadError = (error) => {
  if (!error || typeof error !== "object") return false;
  if (error.name === "ChunkLoadError") return true;
  if (error.code === "CSS_CHUNK_LOAD_FAILED") return true;
  return CHUNK_LOAD_MESSAGE.test(String(error.message || ""));
};

const browserRuntime = () => {
  if (typeof window === "undefined") return null;

  try {
    if (
      !window.location ||
      typeof window.location.reload !== "function" ||
      !window.sessionStorage ||
      typeof window.sessionStorage.getItem !== "function" ||
      typeof window.sessionStorage.setItem !== "function"
    ) {
      return null;
    }
    return {
      location: window.location,
      sessionStorage: window.sessionStorage,
    };
  } catch {
    return null;
  }
};

const reloadWithDurableGuard = (error, runtime) => {
  if (!runtime) throw error;

  const { location, sessionStorage } = runtime;
  const now = Date.now();
  let storedAt;

  try {
    storedAt = Number(sessionStorage.getItem(RELOAD_GUARD_KEY));
  } catch {
    throw error;
  }

  if (
    Number.isFinite(storedAt) &&
    storedAt > 0 &&
    now - storedAt <= RELOAD_GUARD_MS
  ) {
    throw error;
  }

  const guardValue = String(now);
  try {
    sessionStorage.setItem(RELOAD_GUARD_KEY, guardValue);
    if (sessionStorage.getItem(RELOAD_GUARD_KEY) !== guardValue) {
      throw new Error("sessionStorage did not persist the reload guard");
    }
  } catch {
    throw error;
  }

  try {
    location.reload();
  } catch {
    throw error;
  }

  // Never settle: the page is being replaced, and this keeps Suspense from
  // surfacing a fallback or the stale chunk error first.
  return new Promise(() => {});
};

export const loadWithRetry = async (importer, runtime = browserRuntime()) => {
  try {
    return await importer();
  } catch (error) {
    if (!isChunkLoadError(error)) throw error;
  }

  try {
    return await importer();
  } catch (error) {
    if (!isChunkLoadError(error)) throw error;
    return reloadWithDurableGuard(error, runtime);
  }
};

const lazyWithRetry = (importer) => lazy(() => loadWithRetry(importer));

export default lazyWithRetry;
